import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Button, TextField, MenuItem, Select,
  FormControl, InputLabel, FormHelperText, Snackbar, Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkIcon from '@mui/icons-material/Work';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import logo from '../../assets/images/logo.png'; // eslint-disable-line no-unused-vars
import PremiumHeader from '../../components/Header/PremiumHeader';
import { useLang, t } from '../../context/LanguageContext';
import T from '../../translations/index';
import Footer from '../../components/Footer/Footer';
import '../common.css';
import './careers.css';

const AVAILABILITY_OPTIONS = ['Immediate', 'Within 15 Days', 'Within 1 Month'];

const initialForm = {
  fullName: '', mobile: '', email: '', currentLocation: '',
  willingToRelocate: '', workCategory: '', experience: '',
  previousProject: '', availability: '',
  resume: null, idProof: null, certificates: null,
};

const Careers = () => {
  const navigate = useNavigate();
  const { lang } = useLang();

  const WORK_CATEGORIES = lang === 'hi' ? [
    'कुशल श्रमिक', 'अकुशल श्रमिक', 'वेल्डर', 'फिटर',
    'इलेक्ट्रीशियन', 'मैकेनिकल इंजीनियर', 'सुपरवाइज़र', 'सुरक्षा अधिकारी', 'अन्य'
  ] : [
    'Skilled Labour', 'Unskilled Labour', 'Welder', 'Fitter',
    'Electrician', 'Mechanical Engineer', 'Supervisor', 'Safety Officer', 'Other'
  ];

  const EXPERIENCE_OPTIONS_DISPLAY = lang === 'hi' ? ['फ्रेशर', '1–3 वर्ष', '3–5 वर्ष', '5+ वर्ष'] : ['Fresher', '1–3 Years', '3–5 Years', '5+ Years'];
  const AVAILABILITY_OPTIONS_DISPLAY = lang === 'hi' ? ['तत्काल', '15 दिनों के भीतर', '1 महीने के भीतर'] : ['Immediate', 'Within 15 Days', 'Within 1 Month'];

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // eslint-disable-next-line no-unused-vars
  const handleNavClick = (e, path) => {
    e.preventDefault();
    const routes = { home: '/', about: '/about', services: '/services', projects: '/projects', clients: '/clients', contact: '/contact', careers: '/careers', hireus: '/hireus' };
    navigate(routes[path] || '/');
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!/^[6-9]\d{9}$/.test(form.mobile)) e.mobile = 'Enter a valid 10-digit mobile number';
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.currentLocation.trim()) e.currentLocation = 'Location is required';
    if (!form.willingToRelocate) e.willingToRelocate = 'Please select an option';
    if (!form.workCategory) e.workCategory = 'Please select a work category';
    if (!form.experience) e.experience = 'Please select experience level';
    if (!form.availability) e.availability = 'Please select availability';
    return e;
  };

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleFile = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.files[0] || null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    setSubmitError('');

    const fd = new FormData();
    fd.append('fullName', form.fullName);
    fd.append('mobile', form.mobile);
    fd.append('email', form.email || '');
    fd.append('currentLocation', form.currentLocation);
    fd.append('willingToRelocate', form.willingToRelocate);
    fd.append('workCategory', form.workCategory);
    fd.append('experience', form.experience);
    fd.append('previousProject', form.previousProject || '');
    fd.append('availability', form.availability);
    if (form.resume)       fd.append('resume', form.resume);
    if (form.idProof)      fd.append('idProof', form.idProof);
    if (form.certificates) fd.append('certificates', form.certificates);

    try {
      const res = await fetch('/careers_submit.php', { method: 'POST', body: fd });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        setSnackOpen(true);
      } else {
        setSubmitError(data.error || 'Submission failed. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => { setForm(initialForm); setErrors({}); setSubmitted(false); setSubmitError(''); };

  if (submitted) {
    return (
      <div className="routed-page">
        <PremiumHeader />
        <div className="careers-success-screen">
          <div className="success-card">
            <CheckCircleOutlineIcon className="success-icon" />
            <h2>{t(T.appSubmitted, lang)}</h2>
            <p>{t(T.thankYouName, lang)}, <strong>{form.fullName}</strong>! {t(T.appReceived, lang)}</p>
            <p className="success-sub">{t(T.hrContact, lang)} <strong>{form.mobile}</strong> {t(T.businessDays, lang)}</p>
            <div className="success-detail-row">
              <span>{t(T.roleLabel, lang)}</span><span>{form.workCategory}</span>
            </div>
            <div className="success-detail-row">
              <span>{t(T.locationLabel, lang)}</span><span>{form.currentLocation}</span>
            </div>
            <div className="success-detail-row">
              <span>{t(T.availabilityLabel, lang)}</span><span>{form.availability}</span>
            </div>
            <div className="success-actions">
              <Button variant="outlined" onClick={handleReset} className="success-btn-outline">{t(T.submitAnother, lang)}</Button>
              <Button variant="contained" onClick={() => navigate('/')} className="success-btn-filled">{t(T.goToHome, lang)}</Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="routed-page">
      <PremiumHeader />

      {/* Careers Hero */}
      <section className="careers-hero-banner">
        <div className="careers-hero-bg" />
        <div className="careers-hero-overlay" />
        <div className="careers-hero-content">
          <span className="careers-hero-eyebrow">Join Our Team</span>
          <h1 className="careers-hero-title">Build India's Power Future<br/><span>With TCPL</span></h1>
          <p className="careers-hero-sub">Work on landmark thermal power projects across India with a team of 500+ skilled professionals</p>
        </div>
      </section>

      {/* Open Roles */}
      <section className="careers-roles-section">
        <div className="careers-roles-inner">
          <div className="careers-roles-eyebrow">Hiring Now</div>
          <h2 className="careers-roles-title">Roles We're Looking For</h2>
          <p className="careers-roles-sub">We recruit for skilled, semi-skilled, and technical roles for thermal power plant and industrial construction projects across India.</p>
          <div className="careers-roles-grid">
            {[
              { icon: '🔥', name: 'Boiler Welder (IBR)', desc: 'Perform high-pressure pipe and boiler welding work as per IBR regulations. Must have IBR welder certificate.', skills: ['IBR', 'TIG/MIG', 'SMAW', 'NDT Aware'] },
              { icon: '⚙️', name: 'Mechanical Fitter', desc: 'Equipment fitting, alignment, and mechanical assembly for boilers, turbines, and auxiliary systems.', skills: ['Alignment', 'Assembly', 'Torquing', 'Grouting'] },
              { icon: '🏗️', name: 'Site Supervisor', desc: 'Supervise workforce at site, coordinate with PMC/client engineers, maintain daily progress reports.', skills: ['Supervision', 'DPR', 'QA/QC', 'Safety'] },
              { icon: '🛡️', name: 'Safety Officer', desc: 'Implement HSE plan, conduct toolbox talks, ensure OSHA compliance across all work areas.', skills: ['NEBOSH', 'HSE', 'First Aid', 'Permit-to-Work'] },
              { icon: '🔩', name: 'Rigger / Crane Operator', desc: 'Heavy lift rigging, crane operation for equipment erection in power plant and industrial sites.', skills: ['Heavy Lift', 'Rigging', 'ASME B30', 'Load Chart'] },
              { icon: '📊', name: 'Project Engineer', desc: 'Coordinate project execution, track milestones, liaise with clients, prepare BOQ and progress reports.', skills: ['AutoCAD', 'BOQ', 'Planning', 'MS Project'] },
            ].map((role, i) => (
              <div className="careers-role-card" key={i}>
                <span className="careers-role-icon">{role.icon}</span>
                <div className="careers-role-name">{role.name}</div>
                <p className="careers-role-desc">{role.desc}</p>
                <div className="careers-role-skills">
                  {role.skills.map((s, j) => <span className="careers-role-skill" key={j}>{s}</span>)}
                </div>
              </div>
            ))}
          </div>

          <div className="careers-roles-eyebrow" style={{marginTop:'40px', display:'block'}}>Why Join TCPL</div>
          <h2 className="careers-roles-title">What We Offer Our Team</h2>
          <div className="careers-why-grid">
            {[
              { icon: '💰', title: 'Timely Salary', desc: 'Guaranteed on-time salary payment with ESI, PF, and other statutory benefits.' },
              { icon: '🌍', title: 'Pan-India Projects', desc: 'Work on prestigious power projects across 8+ states with career-defining challenges.' },
              { icon: '📈', title: 'Career Growth', desc: 'Clear progression path from skilled labour to supervisor, foreman, and site engineer.' },
              { icon: '🛡️', title: 'Safe Worksite', desc: 'Zero-accident culture with full PPE, safety training, and dedicated HSE team.' },
            ].map((item, i) => (
              <div className="careers-why-card" key={i}>
                <span className="careers-why-icon">{item.icon}</span>
                <div className="careers-why-title">{item.title}</div>
                <div className="careers-why-desc">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="page-container content-wrapper">
        <div className="back-button-container">
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>{t(T.back, lang)}</Button>
        </div>

        <div className="careers-form-card">
          <form onSubmit={handleSubmit} noValidate>

            <div className="form-section-label"><span className="form-section-dot" />{t(T.basicDetails, lang)}</div>
            <div className="form-grid-2">
              <TextField label={t(T.fullNameLabel, lang)} value={form.fullName} onChange={handleChange('fullName')}
                error={!!errors.fullName} helperText={errors.fullName} fullWidth variant="outlined" className="careers-field" />
              <TextField label={t(T.mobileLabel, lang)} value={form.mobile} onChange={handleChange('mobile')}
                error={!!errors.mobile} helperText={errors.mobile} fullWidth variant="outlined" inputProps={{ maxLength: 10 }} className="careers-field" />
            </div>
            <div className="form-grid-2">
              <TextField label={t(T.emailOptional, lang)} value={form.email} onChange={handleChange('email')}
                error={!!errors.email} helperText={errors.email} fullWidth variant="outlined" className="careers-field" />
              <TextField label={t(T.currentLocationLabel, lang)} value={form.currentLocation} onChange={handleChange('currentLocation')}
                error={!!errors.currentLocation} helperText={errors.currentLocation} fullWidth variant="outlined" className="careers-field" />
            </div>

            <div className="form-section-label"><span className="form-section-dot" />{t(T.workPreference, lang)}</div>
            <div className="form-grid-3">
              <FormControl fullWidth error={!!errors.willingToRelocate} className="careers-field">
                <InputLabel>{t(T.willingToRelocate, lang)}</InputLabel>
                <Select value={form.willingToRelocate} onChange={handleChange('willingToRelocate')} label={t(T.willingToRelocate, lang)}>
                  <MenuItem value="Yes">{lang === 'hi' ? 'हां' : 'Yes'}</MenuItem>
                  <MenuItem value="No">{lang === 'hi' ? 'नहीं' : 'No'}</MenuItem>
                </Select>
                {errors.willingToRelocate && <FormHelperText>{errors.willingToRelocate}</FormHelperText>}
              </FormControl>
              <FormControl fullWidth error={!!errors.workCategory} className="careers-field">
                <InputLabel>{t(T.workCategoryLabel, lang)}</InputLabel>
                <Select value={form.workCategory} onChange={handleChange('workCategory')} label={t(T.workCategoryLabel, lang)}>
                  {WORK_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
                {errors.workCategory && <FormHelperText>{errors.workCategory}</FormHelperText>}
              </FormControl>
              <FormControl fullWidth error={!!errors.experience} className="careers-field">
                <InputLabel>{t(T.experienceLabel, lang)}</InputLabel>
                <Select value={form.experience} onChange={handleChange('experience')} label={t(T.experienceLabel, lang)}>
                  {EXPERIENCE_OPTIONS_DISPLAY.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                </Select>
                {errors.experience && <FormHelperText>{errors.experience}</FormHelperText>}
              </FormControl>
            </div>
            <TextField label={t(T.prevProject, lang)} value={form.previousProject} onChange={handleChange('previousProject')}
              fullWidth variant="outlined" className="careers-field" placeholder={t(T.prevProjectPlaceholder, lang)} />

            <div className="form-section-label"><span className="form-section-dot" />{t(T.availabilitySection, lang)}</div>
            <div className="availability-options">
              {AVAILABILITY_OPTIONS.map((opt, idx) => (
                <button type="button" key={opt}
                  className={`avail-chip ${form.availability === opt ? 'avail-chip-selected' : ''}`}
                  onClick={() => { setForm(p => ({ ...p, availability: opt })); setErrors(p => ({ ...p, availability: '' })); }}>
                  {opt === 'Immediate' ? '⚡' : opt === 'Within 15 Days' ? '📅' : '🗓️'} {AVAILABILITY_OPTIONS_DISPLAY[idx]}
                </button>
              ))}
              {errors.availability && <span className="avail-error">{errors.availability}</span>}
            </div>

            <div className="form-section-label"><span className="form-section-dot" />{t(T.uploadDocuments, lang)}</div>
            <div className="upload-grid">
              {[
                { field: 'resume', icon: '📄', titleKey: 'resumeTitle', hintKey: 'resumeHint', accept: '.pdf,.doc,.docx' },
                { field: 'idProof', icon: '🪪', titleKey: 'idProofTitle', hintKey: 'idProofHint', accept: '.pdf,.jpg,.jpeg,.png' },
                { field: 'certificates', icon: '🎓', titleKey: 'certsTitle', hintKey: 'certsHint', accept: '.pdf,.jpg,.jpeg,.png' },
              ].map(({ field, icon, titleKey, hintKey, accept }) => (
                <div className="upload-box" key={field}>
                  <label className="upload-label">
                    <span className="upload-icon">{icon}</span>
                    <span className="upload-title">{t(T[titleKey], lang)}</span>
                    <span className="upload-hint">{t(T[hintKey], lang)}</span>
                    <input type="file" accept={accept} onChange={handleFile(field)} className="upload-input" />
                    {form[field] && <span className="upload-filename">{form[field].name}</span>}
                  </label>
                </div>
              ))}
            </div>

            <div className="form-submit-row">
              <Button type="submit" variant="contained" className="submit-btn" size="large" disabled={submitting}>
                {submitting ? t(T.submitting, lang) : t(T.submitApplication, lang)}
              </Button>
              {submitError && <p className="form-error-msg" style={{color:'#d32f2f',marginTop:'8px',fontSize:'0.9rem'}}>⚠️ {submitError}</p>}
              <p className="form-note">{t(T.needHelp, lang)} <a href="tel:+916200247745">+91 6200247745</a></p>
            </div>
          </form>
        </div>

        <div className="careers-info-strip">
          {[
            { icon: '⚡', titleKey: 'quickResponse', descKey: 'quickResponseDesc' },
            { icon: '🔒', titleKey: 'confidential', descKey: 'confidentialDesc' },
            { icon: '🌍', titleKey: 'panIndiaProjects', descKey: 'panIndiaDesc' },
            { icon: '💰', titleKey: 'timelyPayment', descKey: 'timelyPaymentDesc' },
          ].map(({ icon, titleKey, descKey }) => (
            <div className="careers-info-item" key={titleKey}>
              <span>{icon}</span><strong>{t(T[titleKey], lang)}</strong><span>{t(T[descKey], lang)}</span>
            </div>
          ))}
        </div>
      </div>

      <Footer />

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSnackOpen(false)}>
          {t(T.appSubmittedSnack, lang)}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Careers;
